import React from 'react'; // eslint-disable-line @typescript-eslint/no-unused-vars -- required for UMD global
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { observationsApi } from '../../services/api';
import { queryKeys } from '../../lib/queryClient';
import { useToast } from '../../components/ui';
import { extractApiErrorMessage } from '../../utils/apiError';
import {
  Observation,
  ObservationCreate,
  ObservationUpdate,
  ObservationFilters,
} from '../../types/api';
import type {
  ObservationSuggestRequest,
  ObservationSuggestResponse,
} from '../../services/api/observations';

/**
 * Hook to fetch observations with filters
 */
export const useObservations = (filters?: ObservationFilters) => {
  return useQuery({
    queryKey: queryKeys.observations(filters),
    queryFn: () => observationsApi.getObservations(filters),
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hook to fetch a specific observation
 */
export const useObservation = (observationId: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.observation(observationId),
    queryFn: () => observationsApi.getObservation(observationId),
    enabled: enabled && !!observationId,
  });
};

/**
 * Hook to create a new observation
 */
export const useCreateObservation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  
  return useMutation({
    mutationFn: (data: ObservationCreate) => observationsApi.createObservation(data),
    onSuccess: (data) => {
      // Invalidate observations list
      queryClient.invalidateQueries({ queryKey: queryKeys.observations() });
      
      // Invalidate child-specific observations
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.observationsByChild(data.child_id) 
      });
      
      // Show success message
      addToast({
        title: 'Observation created',
        message: `Observation for ${data.child?.name || 'child'} has been created successfully`,
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: 'Failed to create observation',
        message: extractApiErrorMessage(error, 'An error occurred'),
        type: 'error',
      });
    },
  });
};

/**
 * Hook to update an observation
 */
export const useUpdateObservation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  
  return useMutation({
    mutationFn: ({ observationId, data }: { observationId: string; data: ObservationUpdate }) => 
      observationsApi.updateObservation(observationId, data),
    onSuccess: (data, variables) => {
      // Update cache for specific observation
      queryClient.setQueryData(queryKeys.observation(variables.observationId), data);
      
      // Invalidate observations list
      queryClient.invalidateQueries({ queryKey: queryKeys.observations() });
      
      // Invalidate child-specific observations
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.observationsByChild(data.child_id) 
      });
      
      // Show success message
      addToast({
        title: 'Observation updated',
        message: `Observation has been updated successfully`,
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: 'Failed to update observation',
        message: extractApiErrorMessage(error, 'An error occurred'),
        type: 'error',
      });
    },
  });
};

/**
 * Hook to delete an observation
 */
export const useDeleteObservation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  
  return useMutation({
    mutationFn: (observationId: string) => observationsApi.deleteObservation(observationId),
    onSuccess: (_, observationId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.observation(observationId) });
      
      // Invalidate observations list
      queryClient.invalidateQueries({ queryKey: queryKeys.observations() });
      
      // Show success message
      addToast({
        title: 'Observation deleted',
        message: 'The observation has been removed successfully',
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: 'Failed to delete observation',
        message: extractApiErrorMessage(error, 'An error occurred'),
        type: 'error',
      });
    },
  });
};

/**
 * Hook to publish an observation
 */
export const usePublishObservation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  
  return useMutation({
    mutationFn: (observationId: string) => observationsApi.publishObservation(observationId),
    onSuccess: (_, observationId) => {
      // Update the observation in cache
      queryClient.setQueryData(
        queryKeys.observation(observationId),
        (old: Observation | undefined) => old ? { ...old, is_published: true } : old
      );
      
      // Invalidate observations list
      queryClient.invalidateQueries({ queryKey: queryKeys.observations() });
      
      // Show success message
      addToast({
        title: 'Observation published',
        message: 'The observation has been published successfully',
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: 'Failed to publish observation',
        message: extractApiErrorMessage(error, 'An error occurred'),
        type: 'error',
      });
    },
  });
};

/**
 * Hook to unpublish an observation
 */
export const useUnpublishObservation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  
  return useMutation({
    mutationFn: (observationId: string) => observationsApi.unpublishObservation(observationId),
    onSuccess: (_, observationId) => {
      // Update the observation in cache
      queryClient.setQueryData(
        queryKeys.observation(observationId),
        (old: Observation | undefined) => old ? { ...old, is_published: false } : old
      );
      
      // Invalidate observations list
      queryClient.invalidateQueries({ queryKey: queryKeys.observations() });
      
      // Show success message
      addToast({
        title: 'Observation unpublished',
        message: 'The observation has been unpublished successfully',
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: 'Failed to unpublish observation',
        message: extractApiErrorMessage(error, 'An error occurred'),
        type: 'error',
      });
    },
  });
};

/**
 * Hook to verify an observation
 */
export const useVerifyObservation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  
  return useMutation({
    mutationFn: (observationId: string) => observationsApi.verifyObservation(observationId),
    onSuccess: (_, observationId) => {
      // Update the observation in cache
      queryClient.setQueryData(
        queryKeys.observation(observationId),
        (old: Observation | undefined) => old ? { 
          ...old, 
          is_verified: true, 
          verified_at: new Date().toISOString() 
        } : old
      );
      
      // Invalidate observations list
      queryClient.invalidateQueries({ queryKey: queryKeys.observations() });
      
      // Show success message
      addToast({
        title: 'Observation verified',
        message: 'The observation has been verified successfully',
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: 'Failed to verify observation',
        message: extractApiErrorMessage(error, 'An error occurred'),
        type: 'error',
      });
    },
  });
};

/**
 * Hook to fetch observations for a specific child
 */
export const useObservationsByChild = (childId: string, filters?: Omit<ObservationFilters, 'child_id'>, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.observationsByChild(childId, filters),
    queryFn: () => observationsApi.getObservationsByChild(childId, filters),
    enabled: enabled && !!childId,
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hook to fetch current user's observations
 */
export const useMyObservations = (filters?: ObservationFilters) => {
  return useQuery({
    queryKey: queryKeys.myObservations(filters),
    queryFn: () => observationsApi.getMyObservations(filters),
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hook to fetch recent observations
 */
export const useRecentObservations = (limit: number = 10) => {
  return useQuery({
    queryKey: queryKeys.recentObservations(limit),
    queryFn: () => observationsApi.getRecentObservations(limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch milestone observations
 */
export const useMilestoneObservations = (filters?: Omit<ObservationFilters, 'is_milestone'>) => {
  return useQuery({
    queryKey: queryKeys.milestoneObservations(filters),
    queryFn: () => observationsApi.getMilestoneObservations(filters),
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hook to fetch concern observations
 */
export const useConcernObservations = (filters?: Omit<ObservationFilters, 'is_concern'>) => {
  return useQuery({
    queryKey: queryKeys.concernObservations(filters),
    queryFn: () => observationsApi.getConcernObservations(filters),
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hook to fetch observations by category
 */
export const useObservationsByCategory = (category: string, filters?: Omit<ObservationFilters, 'category'>) => {
  return useQuery({
    queryKey: queryKeys.observationsByCategory(category, filters),
    queryFn: () => observationsApi.getObservationsByCategory(category, filters),
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hook to get observation statistics
 */
export const useObservationStats = (filters?: ObservationFilters) => {
  const { data, isLoading, error } = useObservations(filters);
  
  const stats = React.useMemo(() => {
    if (!data?.items) return null;
    
    const observations = data.items;
    
    return {
      total: observations.length,
      milestones: observations.filter(obs => obs.is_milestone).length,
      concerns: observations.filter(obs => obs.is_concern).length,
      followUpRequired: observations.filter(obs => obs.follow_up_required).length,
      published: observations.filter(obs => obs.is_published).length,
      verified: observations.filter(obs => obs.is_verified).length,
      byCategory: {
        general: observations.filter(obs => obs.category === 'general').length,
        academic: observations.filter(obs => obs.category === 'academic').length,
        social: observations.filter(obs => obs.category === 'social').length,
        emotional: observations.filter(obs => obs.category === 'emotional').length,
        physical: observations.filter(obs => obs.category === 'physical').length,
      },
    };
  }, [data?.items]);
  
  return { stats, isLoading, error };
};

/**
 * Ask the local LLM (via Ollama on the API) for rubric-driven suggestions
 * on an observation draft. Mutation rather than query because the call is
 * expensive (~5-15s) and should only fire on explicit user action.
 *
 * Server returns 200 with `suggestions: []` on Ollama failure -- the UI
 * treats that identically to "the AI thinks the observation is fine".
 */
export const useSuggestObservation = () => {
  return useMutation<ObservationSuggestResponse, Error, ObservationSuggestRequest>({
    mutationFn: (req) => observationsApi.suggestImprovements(req),
  });
};
